import { z } from 'zod'
import { api } from '@/shared/api/client'
import { uuidSchema } from '@/shared/api/schemas'

export const CONTRACT_KINDS = ['AMANAT', 'GUARANTEE', 'SALE', 'OWN_DEVICE', 'RENTAL'] as const
export type ContractKind = (typeof CONTRACT_KINDS)[number]

export const CONTRACT_STATUSES = ['ACTIVE', 'EXPIRING', 'EXPIRED', 'CANCELLED', 'ALL'] as const
export type ContractStatus = (typeof CONTRACT_STATUSES)[number]

export const CONTRACT_TERMS = [1, 2, 3] as const
export const EXPIRING_DAYS = 30

const amount = z.number().int().nullish()

const contractSchema = z.object({
  id: uuidSchema,
  subscriberId: uuidSchema,
  subscriberLogin: z.string(),
  subscriberName: z.string().nullish(),
  number: z.string(),
  kind: z.enum(CONTRACT_KINDS),
  state: z.enum(['ACTIVE', 'CANCELLED']),
  startsOn: z.string(),
  endsOn: z.string(),
  termYears: z.number().int(),
  routerType: z.string().nullish(),
  receiverDevice: z.string().nullish(),
  deviceMac: z.string().nullish(),
  cableLengthM: z.number().int().nullish(),
  packagePrice: amount,
  routerPrice: amount,
  devicePrice: amount,
  cablePrice: amount,
  installationTotal: amount,
  paidAmount: amount,
  remainingAmount: amount,
  depositAmount: amount,
  monthlyRental: amount,
  currency: z.string(),
  guarantorName: z.string().nullish(),
  guarantorRelation: z.string().nullish(),
  guarantorPhone: z.string().nullish(),
  note: z.string().nullish(),
  createdAt: z.string(),
  cancelledAt: z.string().nullish(),
  cancelReason: z.string().nullish(),
  rentalNextOn: z.string().nullish(),
})
export type Contract = z.infer<typeof contractSchema>

export type ContractDraft = Partial<
  Pick<
    Contract,
    | 'routerType' | 'receiverDevice' | 'deviceMac' | 'cableLengthM' | 'packagePrice' | 'routerPrice' | 'devicePrice'
    | 'cablePrice' | 'installationTotal' | 'paidAmount' | 'remainingAmount' | 'depositAmount' | 'monthlyRental'
    | 'guarantorName' | 'guarantorRelation' | 'guarantorPhone' | 'note'
  >
> & { kind: ContractKind; termYears: number; startsOn?: string | null }

const personSchema = z.object({
  personKind: z.enum(['INDIVIDUAL', 'LEGAL']).nullish(),
  companyName: z.string().nullish(),
  licenseNumber: z.string().nullish(),
  directorName: z.string().nullish(),
  signerPosition: z.string().nullish(),
  nationalId: z.string().nullish(),
  occupation: z.string().nullish(),
  education: z.string().nullish(),
  familyPhone: z.string().nullish(),
  installVillage: z.string().nullish(),
  installStreet: z.string().nullish(),
  installHouse: z.string().nullish(),
})
export type PersonDetails = z.infer<typeof personSchema>

const pageSchema = z.object({ rows: z.array(contractSchema), total: z.number(), page: z.number(), size: z.number() })
export type ContractPage = z.infer<typeof pageSchema>

const templateSchema = z.object({ terms: z.string().nullish(), letterheadUrl: z.string().nullish() })
export type ContractTemplate = z.infer<typeof templateSchema>

export async function fetchContracts(
  query: { status: ContractStatus; search?: string; page: number; size: number },
  signal?: AbortSignal,
): Promise<ContractPage> {
  const response = await api.get<unknown>('/api/subscribers/contracts', {
    params: { ...query, ...(query.search?.trim() ? { search: query.search.trim() } : { search: undefined }) },
    ...(signal ? { signal } : {}),
  })
  return pageSchema.parse(response.data)
}

export async function fetchSubscriberContracts(subscriberId: string, signal?: AbortSignal): Promise<Contract[]> {
  const response = await api.get<unknown>(`/api/subscribers/${subscriberId}/contracts`, { ...(signal ? { signal } : {}) })
  return z.array(contractSchema).parse(response.data)
}

export async function createContract(subscriberId: string, draft: ContractDraft): Promise<Contract> {
  const response = await api.post<unknown>(`/api/subscribers/${subscriberId}/contracts`, draft)
  return contractSchema.parse(response.data)
}

export async function updateContract(id: string, draft: ContractDraft): Promise<Contract> {
  const response = await api.put<unknown>(`/api/subscribers/contracts/${id}`, draft)
  return contractSchema.parse(response.data)
}

export async function renewContract(id: string, termYears: number): Promise<Contract> {
  const response = await api.post<unknown>(`/api/subscribers/contracts/${id}/renew`, { termYears })
  return contractSchema.parse(response.data)
}

export async function cancelContract(id: string, reason: string): Promise<Contract> {
  const response = await api.post<unknown>(`/api/subscribers/contracts/${id}/cancel`, { reason })
  return contractSchema.parse(response.data)
}

export async function fetchPerson(subscriberId: string, signal?: AbortSignal): Promise<PersonDetails> {
  const response = await api.get<unknown>(`/api/subscribers/${subscriberId}/person`, { ...(signal ? { signal } : {}) })
  return personSchema.parse(response.data)
}

export async function savePerson(subscriberId: string, person: PersonDetails): Promise<PersonDetails> {
  const response = await api.put<unknown>(`/api/subscribers/${subscriberId}/person`, person)
  return personSchema.parse(response.data)
}

export async function fetchContractTemplate(signal?: AbortSignal): Promise<ContractTemplate> {
  const response = await api.get<unknown>('/api/tenant-settings/contract', { ...(signal ? { signal } : {}) })
  return templateSchema.parse(response.data)
}

export async function saveContractTerms(terms: string | null): Promise<ContractTemplate> {
  const response = await api.put<unknown>('/api/tenant-settings/contract/terms', { terms })
  return templateSchema.parse(response.data)
}

export async function uploadLetterhead(file: File): Promise<ContractTemplate> {
  const form = new FormData()
  form.append('file', file)
  const response = await api.post<unknown>('/api/tenant-settings/contract/letterhead', form, {
    headers: { 'Content-Type': undefined },
  })
  return templateSchema.parse(response.data)
}

export async function removeLetterhead(): Promise<ContractTemplate> {
  const response = await api.delete<unknown>('/api/tenant-settings/contract/letterhead')
  return templateSchema.parse(response.data)
}

/** Faol shartnoma muddati: tugagan, yaqin (30 kun) yoki oddiy. */
export function expiryState(contract: Contract, today: string): 'expired' | 'expiring' | 'ok' | 'cancelled' {
  if (contract.state === 'CANCELLED') return 'cancelled'
  if (contract.endsOn < today) return 'expired'
  const soon = new Date(today)
  soon.setDate(soon.getDate() + EXPIRING_DAYS)
  return contract.endsOn <= soon.toISOString().slice(0, 10) ? 'expiring' : 'ok'
}

const placeSchema = z.object({
  present: z.boolean(),
  contact: z
    .object({ regionName: z.string().nullish(), districtName: z.string().nullish(), address: z.string().nullish() })
    .nullish(),
})
export interface SubscriberPlace {
  regionName: string | null
  districtName: string | null
  address: string | null
}

export async function fetchSubscriberPlace(subscriberId: string): Promise<SubscriberPlace> {
  const response = await api.get<unknown>(`/api/subscribers/${subscriberId}/contact`)
  const parsed = placeSchema.parse(response.data)
  return {
    regionName: parsed.contact?.regionName ?? null,
    districtName: parsed.contact?.districtName ?? null,
    address: parsed.contact?.address ?? null,
  }
}
